'use client'

import { useRouter } from 'next/navigation'
import { useEffect } from 'react'
import debounce from 'lodash/debounce'
import { useEventEmitterContext } from '@/lib/contexts/EventEmitterContext'
import useSearch from '@/lib/hooks/useSearch'

/**
 * This component is responsible for setting up the search callback for external apps.
 */
export default function SetupSearchCallback() {
  const router = useRouter()
  const appEvent$ = useEventEmitterContext()
  const { query: currentQuery, mainCategorySlug: currentMainCategory } = useSearch()

  useEffect(() => {
    // Check if the window object is available (i.e., we're in the browser)
    if (typeof window === 'undefined') {
      return
    }

    console.debug('Setting up search callback')
    window.pcSearchCallback = debounce((query?: string, mainCategory?: string) => {
      console.debug('pcSearchCallback called', { query, mainCategory })
      appEvent$.emit({
        type: 'search:callback',
        payload: {
          query: query ?? '',
          mainCategory: mainCategory ?? 'all',
        },
      })
    }, 250)

    // Cleanup function to remove the callback when the component unmounts
    return () => {
      console.debug('Cleaning up search callback')
      window.pcSearchCallback = undefined
    }
  }, [appEvent$, router])

  appEvent$.useSubscription((evt) => {
    if (evt.type === 'search:callback') {
      const { query, mainCategory } = evt.payload
      console.debug(`Search callback triggered with query: "${query}" and main category: ${mainCategory}`)
      if (query !== currentQuery || mainCategory !== (currentMainCategory ?? 'all')) {
        router.replace(`/search/${mainCategory ?? 'all'}/${encodeURIComponent(query ?? '')}`)
      }
    }
  })

  return null
}
